import React, { useEffect, useMemo, useState } from "react";
import { Flex, Text, Select, Button, Dialog, Switch, TextField } from "@radix-ui/themes";
import { ActiveIntegrateCard, PermissionAlertDialog } from "components";
import {
  useGetBloomreachMappings,
  useSaveBloomreachMappings,
  useUpdateBloomreach,
  useDeactivateBloomreach,
  useUpdateBloomreachFieldMappings,
  useGetAllBrands,
} from "hooks";
import { updateBloomreachConsentSettings } from "../../../api/bBloomreachApi";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";
import { BloomreachConsentSection } from "./BloomreachConsentSection";
import { BloomreachDataFieldsSection } from "./BloomreachDataFieldsSection";
import { normalizeBloomreachFieldMappings } from "./bloomreachFieldMappings";
import {
  BLOOMREACH_SKIP,
  BLOOMREACH_SYNC,
  BloomreachColumnGrid,
  BloomreachLocationRow,
  BloomreachMappingHeader,
} from "./BloomreachLocationRow";

function locationId(loc) {
  return String(loc.id ?? loc.value ?? loc.location_id);
}

export function ActiveBloomreachIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const isOwner = userProfile?.user_role === "Owner";

  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const [apiBaseUrl, setApiBaseUrl] = useState("https://api.exponea.com");
  const [projectToken, setProjectToken] = useState("");
  const [apiKeyId, setApiKeyId] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [originalCredentials, setOriginalCredentials] = useState({
    apiBaseUrl: "https://api.exponea.com",
    projectToken: "",
    apiKeyId: "",
    apiSecret: "",
  });
  const [isCredentialsSaving, setIsCredentialsSaving] = useState(false);
  const [credentialsValid, setCredentialsValid] = useState(true);
  const [credentialsMessage, setCredentialsMessage] = useState(null);

  const [syncConsent, setSyncConsent] = useState(true);
  const [isConsentSaving, setIsConsentSaving] = useState(false);
  const [fieldMappings, setFieldMappings] = useState(() => normalizeBloomreachFieldMappings(null));
  const [originalFieldMappings, setOriginalFieldMappings] = useState(() =>
    normalizeBloomreachFieldMappings(null)
  );
  const [isFieldsSaving, setIsFieldsSaving] = useState(false);
  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
  const [isIndependentSaving, setIsIndependentSaving] = useState(false);

  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);
  const { data: mappingsData } = useGetBloomreachMappings(userProfile?.organisation_id);
  const saveMappings = useSaveBloomreachMappings();
  const deactivate = useDeactivateBloomreach();
  const updateBloomreach = useUpdateBloomreach();
  const updateFieldMappings = useUpdateBloomreachFieldMappings();

  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});
  const [restaurantNames, setRestaurantNames] = useState({});
  const [originalRestaurantNames, setOriginalRestaurantNames] = useState({});
  const [consentCategories, setConsentCategories] = useState([]);
  const [originalConsentCategories, setOriginalConsentCategories] = useState([]);
  const [brandListSelections, setBrandListSelections] = useState({});

  useEffect(() => {
    if (!brandsData || !locationsData) return;
    const defaultBrand = brandsData.find((b) => b.is_default);
    setBconnectBrands(brandsData.filter((b) => !b.is_default) || []);
    setIndependentLocations(
      locationsData.filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      ) || []
    );
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    (bconnectBrands || []).forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      map[b.id] =
        sourceBrand && Array.isArray(sourceBrand.locations)
          ? sourceBrand.locations
          : (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  useEffect(() => {
    const mappings = mappingsData?.data?.mappings;
    if (!mappings) return;

    const nextBase = mappings.api_base_url || "https://api.exponea.com";
    const nextToken = mappings.project_token || "";
    const nextKey = mappings.api_key_id || "";
    const nextSecret = mappings.api_secret_masked || "";
    setApiBaseUrl(nextBase);
    setProjectToken(nextToken);
    setApiKeyId(nextKey);
    setApiSecret(nextSecret);
    setOriginalCredentials({
      apiBaseUrl: nextBase,
      projectToken: nextToken,
      apiKeyId: nextKey,
      apiSecret: nextSecret,
    });
    setSyncConsent(mappings.sync_consent !== false);
    const nextConsent = Array.isArray(mappings.consent_categories)
      ? mappings.consent_categories
      : [];
    setConsentCategories(nextConsent);
    setOriginalConsentCategories(nextConsent);

    const nextFields = normalizeBloomreachFieldMappings(mappings.field_mappings);
    setFieldMappings(nextFields);
    setOriginalFieldMappings(nextFields);

    const savedRows = mappings.location_mappings || [];
    const synced = new Set(savedRows.map((row) => String(row.bconnect_location_id)));
    const savedNames = {};
    savedRows.forEach((row) => {
      savedNames[String(row.bconnect_location_id)] = row.restaurant_name || row.location_name || "";
    });
    const allLocations = [
      ...(independentLocations || []),
      ...Object.values(brandLocationsMap || {}).flat(),
    ];
    const next = {};
    const nextNames = {};
    allLocations.forEach((loc) => {
      const id = locationId(loc);
      next[id] = synced.has(id) ? BLOOMREACH_SYNC : BLOOMREACH_SKIP;
      nextNames[id] = savedNames[id] || loc.name || loc.label || "";
    });
    setLocationMappings(next);
    setOriginalLocationMappings(next);
    setRestaurantNames(nextNames);
    setOriginalRestaurantNames(nextNames);
  }, [mappingsData, independentLocations, brandLocationsMap]);

  const hasCredentialsChanges = useMemo(() => {
    const secretChanged = apiSecret !== originalCredentials.apiSecret && !apiSecret.includes("*");
    return (
      apiBaseUrl !== originalCredentials.apiBaseUrl ||
      projectToken !== originalCredentials.projectToken ||
      apiKeyId !== originalCredentials.apiKeyId ||
      secretChanged
    );
  }, [apiBaseUrl, projectToken, apiKeyId, apiSecret, originalCredentials]);

  const hasFieldMappingChanges = useMemo(
    () => JSON.stringify(fieldMappings) !== JSON.stringify(originalFieldMappings),
    [fieldMappings, originalFieldMappings]
  );

  const sectionChanged = (locations) =>
    (locations || []).some((loc) => {
      const id = locationId(loc);
      const syncChanged =
        (locationMappings[id] || BLOOMREACH_SKIP) !== (originalLocationMappings[id] || BLOOMREACH_SKIP);
      const nameChanged = (restaurantNames[id] || "") !== (originalRestaurantNames[id] || "");
      return syncChanged || nameChanged;
    });

  const allBrandLocations = useMemo(
    () => Object.values(brandLocationsMap || {}).flat(),
    [brandLocationsMap]
  );
  const hasBrandSectionChanges = useMemo(
    () => sectionChanged(allBrandLocations),
    [allBrandLocations, locationMappings, originalLocationMappings, restaurantNames, originalRestaurantNames]
  );
  const hasIndependentLocationChanges = useMemo(
    () => sectionChanged(independentLocations),
    [independentLocations, locationMappings, originalLocationMappings, restaurantNames, originalRestaurantNames]
  );
  const hasConsentCategoryChanges = useMemo(
    () => JSON.stringify(consentCategories) !== JSON.stringify(originalConsentCategories),
    [consentCategories, originalConsentCategories]
  );

  const handleSaveCredentials = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      return;
    }
    if (!projectToken.trim() || !apiKeyId.trim()) {
      setCredentialsValid(false);
      setCredentialsMessage("Please enter a project token and API Key ID.");
      return;
    }
    setCredentialsValid(true);
    setCredentialsMessage(null);
    setIsCredentialsSaving(true);
    try {
      const payload = {
        organisation_id: userProfile.organisation_id,
        api_base_url: apiBaseUrl.trim() || "https://api.exponea.com",
        project_token: projectToken.trim(),
        api_key_id: apiKeyId.trim(),
      };
      if (apiSecret && !apiSecret.includes("*")) {
        payload.api_secret = apiSecret.trim();
      }
      const res = await updateBloomreach.mutateAsync(payload);
      if (res?.data?.status === "success") {
        queryClient.invalidateQueries(["bloomreach-mappings", userProfile.organisation_id]);
        queryClient.invalidateQueries(["bloomreach-status"]);
        setToastMessage("Bloomreach credentials validated and saved");
      } else {
        const msg = res?.data?.message || "Failed to update Bloomreach credentials.";
        setCredentialsValid(false);
        setCredentialsMessage(msg);
        setToastMessage(msg);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update Bloomreach credentials.";
      setCredentialsValid(false);
      setCredentialsMessage(msg);
      setToastMessage(msg);
    } finally {
      setIsCredentialsSaving(false);
    }
  };

  const handleSaveConsentCategories = async () => {
    if (!isOwner) {
      setShowPermissionAlert(true);
      return;
    }
    setIsConsentSaving(true);
    try {
      const res = await updateBloomreachConsentSettings({
        organisation_id: userProfile.organisation_id,
        consent_categories: consentCategories,
      });
      if (res?.data?.status === "success") {
        const saved = Array.isArray(res.data.consent_categories) ? res.data.consent_categories : [];
        setConsentCategories(saved);
        setOriginalConsentCategories(saved);
        queryClient.invalidateQueries(["bloomreach-mappings", userProfile.organisation_id]);
        setToastMessage("Consent categories saved");
      } else {
        setToastMessage("Failed to update consent categories");
      }
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update consent categories");
    } finally {
      setIsConsentSaving(false);
    }
  };

  const handleConsentToggle = async (checked) => {
    if (!isOwner) {
      setShowPermissionAlert(true);
      return;
    }
    setIsConsentSaving(true);
    try {
      const res = await updateBloomreachConsentSettings({
        organisation_id: userProfile.organisation_id,
        sync_consent: checked,
      });
      if (res?.data?.status === "success") {
        setSyncConsent(Boolean(res.data.sync_consent));
        queryClient.invalidateQueries(["bloomreach-mappings", userProfile.organisation_id]);
        queryClient.invalidateQueries(["bloomreach-status"]);
        setToastMessage(checked ? "Marketing consent sync enabled" : "Marketing consent sync disabled");
      } else {
        setToastMessage("Failed to update consent settings");
      }
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update consent settings");
    } finally {
      setIsConsentSaving(false);
    }
  };

  const handleSaveFieldMappings = async () => {
    setIsFieldsSaving(true);
    try {
      const res = await updateFieldMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        field_mappings: fieldMappings,
      });
      if (res?.data?.status === "success") {
        const saved = normalizeBloomreachFieldMappings(res.data.field_mappings);
        setFieldMappings(saved);
        setOriginalFieldMappings(saved);
        queryClient.invalidateQueries(["bloomreach-mappings", userProfile.organisation_id]);
        setToastMessage("Data field mappings saved successfully");
      } else {
        setToastMessage(res?.data?.message || "Failed to save data field mappings");
      }
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to save data field mappings");
    } finally {
      setIsFieldsSaving(false);
    }
  };

  const handleSaveLocationMappings = async (section) => {
    const savingSetters =
      section === "brands" ? [setIsBrandsSaving] : [setIsIndependentSaving];
    savingSetters.forEach((setter) => setter(true));
    try {
      const allLocations = [
        ...(independentLocations || []),
        ...Object.values(brandLocationsMap || {}).flat(),
      ];
      const locationPayload = allLocations
        .filter((loc) => locationMappings[locationId(loc)] === BLOOMREACH_SYNC)
        .map((loc) => ({
          bconnect_location_id: loc.id ?? loc.value ?? loc.location_id,
          bconnect_location_name: loc.name ?? loc.label ?? "",
          restaurant_name: restaurantNames[locationId(loc)] || loc.name || loc.label || "",
        }));
      if (locationPayload.length === 0) {
        setToastMessage("Select at least one location to sync.");
        return;
      }
      const result = await saveMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        location_mappings: locationPayload,
      });
      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }
      setToastMessage("Location mappings saved successfully");
      queryClient.invalidateQueries(["bloomreach-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["bloomreach-status"]);
    } catch (err) {
      setToastMessage(err?.response?.data?.message || err?.message || "Failed to save location mappings");
    } finally {
      savingSetters.forEach((setter) => setter(false));
    }
  };

  const handleApplyBrand = (brandId) => {
    const selected = brandListSelections[String(brandId)];
    if (!selected) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      brandLocs.forEach((loc) => {
        next[locationId(loc)] = selected;
      });
      return next;
    });
  };

  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      const res = await deactivate.mutateAsync({ organisation_id: userProfile.organisation_id });
      if (res?.data?.status !== "success") {
        throw new Error(res?.data?.message || "Failed to deactivate integration");
      }
      setShowConfirmDialog(false);
      setToastMessage("Bloomreach integration disconnected");
      queryClient.invalidateQueries(["bloomreach-status"]);
    } catch (err) {
      setToastMessage(err?.response?.data?.message || err?.message || "Failed to deactivate Bloomreach");
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleDisconnectClick = () => {
    if (isOwner) setShowConfirmDialog(true);
    else setShowPermissionAlert(true);
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  if (isBrandsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Bloomreach integration data...</Text>
      </Flex>
    );
  }

  const renderBrandApply = (brand, brandLocations) => {
    if (!brandLocations.length) return null;
    const selected = brandListSelections[String(brand.id)] || "";
    const label =
      selected === BLOOMREACH_SYNC ? "Sync" : selected === BLOOMREACH_SKIP ? "Don't sync" : "Apply to brand...";
    return (
      <Flex gap="2" align="center" style={{ minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
        <Select.Root
          value={selected}
          onValueChange={(value) =>
            setBrandListSelections((prev) => ({ ...prev, [String(brand.id)]: value }))
          }
          disabled={!isOwner}
        >
          <Select.Trigger placeholder="Apply to brand..." style={{ width: "100%", boxSizing: "border-box" }}>
            <div
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {label}
            </div>
          </Select.Trigger>
          <Select.Content side="bottom" align="start" sideOffset={4}>
            <Select.Item value={BLOOMREACH_SYNC}>Sync</Select.Item>
            <Select.Item value={BLOOMREACH_SKIP}>Don't sync</Select.Item>
          </Select.Content>
        </Select.Root>
        </div>
        <Button
          size="1"
          variant="soft"
          onClick={() => handleApplyBrand(brand.id)}
          disabled={!isOwner || !selected}
          style={{ flexShrink: 0 }}
        >
          Apply
        </Button>
      </Flex>
    );
  };

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Bloomreach"
        subtitle="Choose locations to sync"
        image_url="bloomreach.jpeg"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        <ActiveIntegrateCard.Section
          name="API credentials"
          subtitle="Update the Bloomreach project token, API Key ID, and API Secret"
        >
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">API base URL</Text>
              <TextField.Root
                value={apiBaseUrl}
                onChange={(event) => {
                  setApiBaseUrl(event.target.value);
                  setCredentialsValid(true);
                }}
                placeholder="https://api.exponea.com"
                disabled={!isOwner}
                size="2"
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Project token</Text>
              <TextField.Root
                value={projectToken}
                onChange={(event) => {
                  setProjectToken(event.target.value);
                  setCredentialsValid(true);
                }}
                placeholder="Project token"
                disabled={!isOwner}
                size="2"
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">API Key ID</Text>
              <TextField.Root
                value={apiKeyId}
                onChange={(event) => {
                  setApiKeyId(event.target.value);
                  setCredentialsValid(true);
                }}
                placeholder="API Key ID"
                disabled={!isOwner}
                size="2"
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">API Secret</Text>
              <TextField.Root
                type="password"
                value={apiSecret}
                onChange={(event) => {
                  setApiSecret(event.target.value);
                  setCredentialsValid(true);
                }}
                placeholder="API Secret"
                disabled={!isOwner}
                size="2"
              />
            </Flex>
            {!credentialsValid && (
              <Text size="1" weight="regular" color="red">
                {credentialsMessage || "Please enter valid credentials."}
              </Text>
            )}
            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSaveCredentials}
                loading={isCredentialsSaving}
                disabled={!isOwner || isCredentialsSaving || !hasCredentialsChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Marketing consent"
          subtitle="The WiFi form collects one marketing opt-in. Choose which Bloomreach categories that opt-in ticks."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" align="center" justify="between" style={{ width: "100%" }}>
              <Text size="2" color="gray">
                Sync marketing consent
              </Text>
              <Switch
                size="2"
                checked={syncConsent}
                disabled={!isOwner || isConsentSaving}
                onCheckedChange={handleConsentToggle}
              />
            </Flex>
            {syncConsent ? (
              <BloomreachConsentSection
                organisationId={userProfile?.organisation_id}
                categories={consentCategories}
                onChange={setConsentCategories}
                disabled={!isOwner}
              />
            ) : null}
            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSaveConsentCategories}
                loading={isConsentSaving}
                disabled={!isOwner || isConsentSaving || !hasConsentCategoryChanges}
              >
                Save categories
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Data fields"
          subtitle="These fields are sent on each WiFi event. Email is always sent."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <BloomreachDataFieldsSection
              fieldMappings={fieldMappings}
              onChange={(source, value) =>
                setFieldMappings((prev) => ({ ...prev, [source]: value }))
              }
              disabled={!isOwner}
            />
            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSaveFieldMappings}
                loading={isFieldsSaving}
                disabled={!isOwner || isFieldsSaving || !hasFieldMappingChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Brands"
          subtitle="Group: b connect brands with each of their locations"
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <BloomreachMappingHeader />
            {(bconnectBrands || []).length > 0 ? (
              bconnectBrands.map((brand) => {
                const brandLocations = brandLocationsMap[brand.id] || [];
                return (
                  <Flex key={brand.id} direction="column" gap="2" style={{ width: "100%" }}>
                    <BloomreachColumnGrid>
                      <Text
                        size="2"
                        weight="medium"
                        title={brand.name}
                        style={{
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        [{brand.name}]
                      </Text>
                      <span />
                      {renderBrandApply(brand, brandLocations) || <span />}
                    </BloomreachColumnGrid>
                    {brandLocations.length > 0 ? (
                      brandLocations.map((loc) => (
                        <BloomreachLocationRow
                          key={locationId(loc)}
                          loc={loc}
                          value={locationMappings[locationId(loc)]}
                          restaurantName={restaurantNames[locationId(loc)] || ""}
                          onRestaurantNameChange={(value) =>
                            setRestaurantNames((prev) => ({ ...prev, [locationId(loc)]: value }))
                          }
                          disabled={!isOwner}
                          onValueChange={(value) =>
                            setLocationMappings((prev) => ({ ...prev, [locationId(loc)]: value }))
                          }
                        />
                      ))
                    ) : (
                      <Text size="2" style={{ color: "var(--gray-a9)" }}>
                        No locations for this brand
                      </Text>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Text size="2" color="gray">No brands found.</Text>
            )}
            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("brands")}
                loading={isBrandsSaving}
                disabled={!isOwner || isBrandsSaving || !hasBrandSectionChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Locations"
          subtitle="The restaurant name starts as the b connect location name. Change it when Bloomreach uses a different name."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <BloomreachMappingHeader />
            {(independentLocations || []).length > 0 ? (
              independentLocations.map((loc) => (
                <BloomreachLocationRow
                  key={locationId(loc)}
                  loc={loc}
                  value={locationMappings[locationId(loc)]}
                  restaurantName={restaurantNames[locationId(loc)] || ""}
                  onRestaurantNameChange={(value) =>
                    setRestaurantNames((prev) => ({ ...prev, [locationId(loc)]: value }))
                  }
                  disabled={!isOwner}
                  onValueChange={(value) =>
                    setLocationMappings((prev) => ({ ...prev, [locationId(loc)]: value }))
                  }
                />
              ))
            ) : (
              <Text size="2" color="gray">No locations found.</Text>
            )}
            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("independent")}
                loading={isIndependentSaving}
                disabled={!isOwner || isIndependentSaving || !hasIndependentLocationChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Bloomreach</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Customer data will stop syncing with Bloomreach.
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button
              variant="solid"
              color="red"
              onClick={handleDeactivate}
              loading={isDeactivating}
              disabled={isDeactivating}
            >
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
